import { Component, computed, inject, input, OnDestroy, OnInit, output, signal } from '@angular/core';
import { delay } from '@gametailors/web-core/misc';
import { RidButton } from '../../components/rid-button/rid-button';
import { RidFeedbackToast } from '../../components/rid-feedback-toast/rid-feedback-toast';
import { RidHorizontalPages } from '../../components/rid-horizontal-pages/rid-horizontal-pages';
import { RidInstructionModal } from '../../components/rid-instruction-modal/rid-instruction-modal';
import { RidMechanicBackground } from '../../components/rid-mechanic-background/rid-mechanic-background';
import { RidMechanicContent } from '../../components/rid-mechanic-content/rid-mechanic-content';
import { RidMechanicTitle } from '../../components/rid-mechanic-title/rid-mechanic-title';
import { RidMechanicWrapper } from '../../components/rid-mechanic-wrapper/rid-mechanic-wrapper';
import { RidModalBackground } from '../../components/rid-modal-background/rid-modal-background';
import { RidRatingModal } from '../../components/rid-rating-modal/rid-rating-modal';
import { RidTimer } from '../../components/rid-timer/rid-timer';
import { RidTopBar } from '../../components/rid-top-bar/rid-top-bar';
import { RidWordMatrix } from '../../components/rid-word-matrix/rid-word-matrix';
import { TRidWord } from '../../components/rid-word/rid-word';
import { IconTimer } from '../../icons/i-timer';
import { PluralPipe } from '../../pipes/plural.pipe';
import { DiceService } from '../../services/dice-service.service';

function doesIndexInclude(index: [matrix: number, col: number, row: number], compareTo: [matrix: number, col: number, row: number]) {
  return (
    index[0] < compareTo[0] ||
    (index[0] === compareTo[0] && index[1] < compareTo[1]) ||
    (index[0] === compareTo[0] && index[1] === compareTo[1] && index[2] <= compareTo[2])
  );
}

@Component({
  selector: 'rid-reading-matrix-timer',
  imports: [
    PluralPipe,
    RidButton,
    RidTopBar,
    RidMechanicBackground,
    RidRatingModal,
    RidFeedbackToast,
    RidInstructionModal,
    RidMechanicTitle,
    RidMechanicWrapper,
    RidMechanicContent,
    RidModalBackground,
    IconTimer,
    RidTimer,
    RidHorizontalPages,
    RidWordMatrix,
  ],
  templateUrl: './rid-reading-matrix-timer.html',
  host: {
    class: 'block',
  },
})
export class RidReadingMatrixTimer implements OnInit, OnDestroy {
  private readonly $diceService = inject(DiceService);

  matrices = input.required<string[][][]>();
  duration = input.required<number>();
  previousWordCount = input<number>();

  index = signal(0);
  state = signal<
    | { type: 'INSTRUCTION' }
    | { type: 'READING' }
    | { type: 'PROGRESS' }
    | { type: 'FEEDBACK'; selection: [matrix: number, col: number, row: number]; words: number }
    | { type: 'RATING'; selection: [matrix: number, col: number, row: number] }
    | { type: 'VIEWING'; selection: [matrix: number, col: number, row: number] }
  >({
    type: 'INSTRUCTION',
  });
  durationLeft = signal<number | undefined>(undefined);
  matricesWithAppearance = computed<(string | TRidWord)[][][]>(() => {
    const state = this.state();

    if (state.type === 'PROGRESS') {
      return this.matrices().map((matrix) => matrix.map((col) => col.map((word) => ({ word, appearance: 'DEFAULT', isSelectable: true }))));
    }

    if ('selection' in state) {
      const selectedWordCount = this.matrices().flatMap((matrix, mIndex) =>
        matrix.flatMap((col, cIndex) => col.filter((_, rIndex) => doesIndexInclude([mIndex, cIndex, rIndex], state.selection))),
      ).length;
      const previousCount = this.previousWordCount();
      const appearance = previousCount !== undefined && selectedWordCount <= previousCount ? 'NEUTRAL' : 'CORRECT';

      return this.matrices().map((matrix, mIndex) =>
        matrix.map((col, cIndex) =>
          col.map((word, rIndex) => ({
            word,
            appearance: doesIndexInclude([mIndex, cIndex, rIndex], state.selection) ? appearance : 'DEFAULT',
            isSelectable: false,
          })),
        ),
      );
    }

    return this.matrices();
  });

  finished = output<void>();

  ngOnInit(): void {
    this.$diceService.isNumericDiceAvailable.set(true);
  }

  ngOnDestroy(): void {
    this.$diceService.isNumericDiceAvailable.set(false);
  }

  start() {
    this.state.set({ type: 'READING' });
    this.durationLeft.set(this.duration());
    this.loop();
  }

  async loop() {
    let duration = this.duration();
    while (this.state().type === 'READING') {
      this.durationLeft.set(duration);
      await delay(1000);
      duration--;

      if (duration < 0) {
        this.finishReading();
      }
    }
  }

  finishReading() {
    this.state.set({ type: 'PROGRESS' });
  }

  select(m: number, index: [col: number, row: number]) {
    const i = [m, ...index] as [matrix: number, col: number, row: number];

    const numberOfWords = this.matricesWithAppearance().flatMap((matrix, mIndex) =>
      matrix.flatMap((col, cIndex) => col.filter((_, rIndex) => doesIndexInclude([mIndex, cIndex, rIndex], i))),
    ).length;
    this.state.set({ type: 'FEEDBACK', selection: i, words: numberOfWords });
  }

  finish() {
    this.finished.emit();
  }
}
